import React, { useEffect, useState, useRef } from "react";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import Select2 from "../../Components/Accounting/Select2";
import { TableSkeleton } from "../../Components/Accounting/SkeletonLoader";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

const BankReconciliation = () => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [bankEntries, setBankEntries] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState([]);
  const [selectedBank, setSelectedBank] = useState([]);
  const [importText, setImportText] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [statementBalance, setStatementBalance] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const fileRef = useRef();

  useEffect(() => { fetchBankAccounts(); }, []);
  useEffect(() => { if (selectedAccount) fetchLedgerEntries(); }, [selectedAccount, fromDate, toDate]);

  const fetchBankAccounts = async () => {
    const res = await ajaxAccounting.listAccounts({ type: "asset" });
    if (res.status === "OK") {
      const bankAccts = (res.details || []).filter((a) => a.account_code?.startsWith("102") || a.sub_type === "bank" || a.name?.toLowerCase().includes("bank"));
      setAccounts(bankAccts);
      if (bankAccts.length > 0) setSelectedAccount(String(bankAccts[0].id));
    }
  };

  const fetchLedgerEntries = async () => {
    setLoading(true);
    const res = await ajaxAccounting.getLedger({ account_id: parseInt(selectedAccount), from_date: fromDate || undefined, to_date: toDate || undefined });
    if (res.status === "OK") {
      const entries = (res.details?.transactions || []).filter((t) => !t.reconciled);
      setLedgerEntries(entries);
    }
    setLoading(false);
  };

  const parseCSV = (text) => {
    const lines = text.trim().split("\n").filter((l) => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/"/g, ""));
    return lines.slice(1).map((line) => {
      const vals = line.split(",").map((v) => v.trim().replace(/"/g, ""));
      const obj = {};
      headers.forEach((h, i) => { obj[h] = vals[i] || ""; });
      return {
        id: Math.random().toString(36).substr(2, 9),
        date: obj.date || obj.txn_date || obj.transaction_date || "",
        description: obj.description || obj.narration || obj.details || obj.particulars || "",
        debit: parseFloat(obj.debit || obj.dr || "0") || 0,
        credit: parseFloat(obj.credit || obj.cr || "0") || 0,
        reference: obj.reference || obj.ref || obj.cheque || "",
        balance: parseFloat(obj.balance || "0") || 0,
      };
    }).filter((r) => r.date);
  };

  const handleImport = () => {
    if (!importText.trim()) { toast.error("Paste bank statement data first."); return; }
    const parsed = parseCSV(importText);
    if (parsed.length === 0) { toast.error("Could not parse the statement. Ensure it's CSV with date, description, debit/credit columns."); return; }
    setBankEntries(parsed);
    toast.success(`Imported ${parsed.length} bank transactions.`);
    setShowImport(false);
  };

  const handleFileImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setImportText(ev.target.result); };
    reader.readAsText(file);
  };

  const toggleLedgerSelect = (id) => {
    setSelectedLedger((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleBankSelect = (id) => {
    setSelectedBank((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const autoMatch = () => {
    const newPairs = [];
    const usedBank = new Set();
    const usedLedger = new Set();
    ledgerEntries.forEach((le) => {
      const leAmt = Math.abs((le.debit || 0) - (le.credit || 0));
      bankEntries.forEach((be) => {
        if (usedBank.has(be.id) || usedLedger.has(le.id)) return;
        const beAmt = Math.abs((be.debit || 0) - (be.credit || 0));
        const dateMatch = le.date && be.date && Math.abs(new Date(le.date) - new Date(be.date)) < 3 * 24 * 60 * 60 * 1000;
        const amtMatch = Math.abs(leAmt - beAmt) < 0.01;
        if (amtMatch && dateMatch) {
          newPairs.push({ ledger_id: le.id, bank_id: be.id, amount: leAmt, le_date: le.date, be_date: be.date, le_desc: le.description, be_desc: be.description });
          usedBank.add(be.id);
          usedLedger.add(le.id);
        }
      });
    });
    setMatchedPairs((prev) => [...prev, ...newPairs]);
    toast.success(`Auto-matched ${newPairs.length} transaction(s).`);
  };

  const manualMatch = () => {
    if (selectedLedger.length === 0 || selectedBank.length === 0) { toast.error("Select at least one ledger and one bank entry."); return; }
    const leAmt = selectedLedger.reduce((s, id) => { const le = ledgerEntries.find((e) => e.id === id); return s + Math.abs((le?.debit || 0) - (le?.credit || 0)); }, 0);
    const beAmt = selectedBank.reduce((s, id) => { const be = bankEntries.find((e) => e.id === id); return s + Math.abs((be?.debit || 0) - (be?.credit || 0)); }, 0);
    if (Math.abs(leAmt - beAmt) > 0.01) { toast.error(`Amounts don't match: Ledger ${functions.formatCurrency(leAmt)} vs Bank ${functions.formatCurrency(beAmt)}`); return; }
    const pair = { ledger_ids: selectedLedger, bank_ids: selectedBank, amount: leAmt, manual: true };
    setMatchedPairs((prev) => [...prev, pair]);
    setSelectedLedger([]);
    setSelectedBank([]);
    toast.success("Manually matched!");
  };

  const unmatch = (idx) => {
    setMatchedPairs((prev) => prev.filter((_, i) => i !== idx));
  };

  const matchedLedgerIds = new Set(matchedPairs.flatMap((p) => p.ledger_ids || (p.ledger_id ? [p.ledger_id] : [])));
  const matchedBankIds = new Set(matchedPairs.flatMap((p) => p.bank_ids || (p.bank_id ? [p.bank_id] : [])));

  const ledgerBalance = ledgerEntries.reduce((s, e) => s + (e.debit || 0) - (e.credit || 0), 0);
  const bankBalance = bankEntries.reduce((s, e) => s + (e.credit || 0) - (e.debit || 0), 0);
  const difference = parseFloat(statementBalance || 0) - ledgerBalance;
  const unmatchedLedger = ledgerEntries.filter((e) => !matchedLedgerIds.has(e.id));
  const unmatchedBank = bankEntries.filter((e) => !matchedBankIds.has(e.id));

  const ledgerCols = [
    { key: "_sel", label: "", render: (_, row) => <input type="checkbox" checked={selectedLedger.includes(row.id)} onChange={() => toggleLedgerSelect(row.id)} disabled={matchedLedgerIds.has(row.id)} /> },
    { key: "date", label: "Date", render: (v) => functions.formatDate(v) },
    { key: "description", label: "Description", render: (v) => <span className="tx-12">{v}</span> },
    { key: "debit", label: "Dr", render: (v) => v > 0 ? <span className="tx-success">{functions.formatCurrency(v)}</span> : "-" },
    { key: "credit", label: "Cr", render: (v) => v > 0 ? <span className="tx-danger">{functions.formatCurrency(v)}</span> : "-" },
    { key: "_status", label: "", render: (_, row) => matchedLedgerIds.has(row.id) ? <span className="badge bg-success">✓</span> : null },
  ];

  const bankCols = [
    { key: "_sel", label: "", render: (_, row) => <input type="checkbox" checked={selectedBank.includes(row.id)} onChange={() => toggleBankSelect(row.id)} disabled={matchedBankIds.has(row.id)} /> },
    { key: "date", label: "Date", render: (v) => functions.formatDate(v) },
    { key: "description", label: "Description", render: (v) => <span className="tx-12">{v}</span> },
    { key: "reference", label: "Ref", render: (v) => <span className="tx-12 tx-muted">{v}</span> },
    { key: "debit", label: "Dr", render: (v) => v > 0 ? <span className="tx-success">{functions.formatCurrency(v)}</span> : "-" },
    { key: "credit", label: "Cr", render: (v) => v > 0 ? <span className="tx-danger">{functions.formatCurrency(v)}</span> : "-" },
    { key: "_status", label: "", render: (_, row) => matchedBankIds.has(row.id) ? <span className="badge bg-success">✓</span> : null },
  ];

  return (
    <AppContainer
      title="Bank Reconciliation"
      previousPageTitle="Accounting"
      description="Compare your ledger bank transactions with your actual bank statement. Import a CSV from your bank, then use Auto-Match or manually select pairs to reconcile. Unmatched items need investigation."
      actions={[
        { label: "Import Statement", icon: "fa-solid fa-file-import", className: "btn btn-primary", onClick: () => setShowImport(true) },
        { label: "Auto-Match", icon: "fa-solid fa-magic", className: "btn btn-outline-success", onClick: autoMatch, disabled: bankEntries.length === 0 },
        { label: "Manual Match", icon: "fa-solid fa-link", className: "btn btn-outline-primary", onClick: manualMatch, disabled: selectedLedger.length === 0 || selectedBank.length === 0 },
      ]}
    >
      <Toaster />

      {/* Header filters */}
      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Bank Account</label>
            <Select2 options={accounts.map((a) => ({ value: String(a.id), label: `${a.account_code} — ${a.name}` }))} value={selectedAccount} onChange={setSelectedAccount} placeholder="Select bank account" />
          </div>
          <div className="col-lg-3">
            <label className="form-label tx-12 fw-semibold">From Date</label>
            <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="col-lg-3">
            <label className="form-label tx-12 fw-semibold">To Date</label>
            <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div className="col-lg-2">
            <label className="form-label tx-12 fw-semibold">Bank Statement Balance</label>
            <input type="number" className="form-control" placeholder="0.00" value={statementBalance} onChange={(e) => setStatementBalance(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="row mb-3">
        {[
          { label: "Ledger Balance", value: ledgerBalance, color: "#6366f1" },
          { label: "Bank Statement Balance", value: parseFloat(statementBalance) || 0, color: "#10b981" },
          { label: "Difference", value: difference, color: Math.abs(difference) < 0.01 ? "#10b981" : "#ef4444" },
          { label: "Matched Items", value: matchedPairs.length, color: "#0ea5e9", type: "count" },
          { label: "Unmatched Ledger", value: unmatchedLedger.length, color: "#f59e0b", type: "count" },
          { label: "Unmatched Bank", value: unmatchedBank.length, color: "#f59e0b", type: "count" },
        ].map((c, i) => (
          <div key={i} className="col-xl-2 col-lg-4 col-sm-6 mb-2">
            <div className="card custom-card p-2" style={{ borderLeft: `3px solid ${c.color}` }}>
              <div className="tx-11 tx-muted mb-1">{c.label}</div>
              <div className="fw-bold" style={{ color: c.color }}>{c.type === "count" ? c.value : functions.formatCurrency(c.value)}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Two-panel comparison */}
      <div className="row">
        <div className="col-lg-6 mb-3">
          <div className="card custom-card p-3">
            <div className="d-flex justify-content-between mb-2">
              <h6 className="fw-semibold mb-0">Ledger Transactions <span className="badge bg-secondary ms-1">{ledgerEntries.length}</span></h6>
              <span className="tx-12 tx-muted">{selectedLedger.length} selected</span>
            </div>
            {loading ? <TableSkeleton rows={5} cols={5} /> : (
              <DataTable columns={ledgerCols} data={unmatchedLedger} loading={false} pageSize={10} emptyMessage="No unmatched ledger entries." />
            )}
          </div>
        </div>

        <div className="col-lg-6 mb-3">
          <div className="card custom-card p-3">
            <div className="d-flex justify-content-between mb-2">
              <h6 className="fw-semibold mb-0">Bank Statement <span className="badge bg-secondary ms-1">{bankEntries.length}</span></h6>
              <span className="tx-12 tx-muted">{selectedBank.length} selected</span>
            </div>
            {bankEntries.length === 0 ? (
              <div className="text-center py-4 tx-muted">
                <i className="fa-solid fa-file-import" style={{ fontSize: "2rem", opacity: 0.3 }}></i>
                <p className="mt-2">Import your bank statement CSV to start reconciling.</p>
                <button className="btn btn-sm btn-primary" onClick={() => setShowImport(true)}>Import Statement</button>
              </div>
            ) : (
              <DataTable columns={bankCols} data={unmatchedBank} loading={false} pageSize={10} emptyMessage="No unmatched bank entries." />
            )}
          </div>
        </div>
      </div>

      {/* Matched pairs */}
      {matchedPairs.length > 0 && (
        <div className="card custom-card p-3 mb-3">
          <h6 className="fw-semibold mb-3 tx-success"><i className="fa-solid fa-link me-1"></i>Matched Pairs ({matchedPairs.length})</h6>
          <table className="table table-sm table-bordered" style={{ fontSize: "0.82rem" }}>
            <thead className="bg-light"><tr><th>#</th><th>Ledger Description</th><th>Bank Description</th><th>Amount</th><th>Type</th><th></th></tr></thead>
            <tbody>
              {matchedPairs.map((p, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{p.le_desc || (Array.isArray(p.ledger_ids) ? `${p.ledger_ids.length} entries` : p.ledger_id)}</td>
                  <td>{p.be_desc || (Array.isArray(p.bank_ids) ? `${p.bank_ids.length} entries` : p.bank_id)}</td>
                  <td className="fw-bold">{functions.formatCurrency(p.amount)}</td>
                  <td><span className={`badge ${p.manual ? "bg-primary" : "bg-info"}`}>{p.manual ? "Manual" : "Auto"}</span></td>
                  <td><button className="btn btn-sm btn-light tx-danger" onClick={() => unmatch(i)} title="Unmatch"><i className="fa-solid fa-unlink"></i></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Import Modal */}
      {showImport && (
        <AppModal
          title="Import Bank Statement"
          size="lg"
          onClose={() => setShowImport(false)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setShowImport(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleImport}><i className="fa-solid fa-file-import me-1"></i>Import</button>
            </>
          }
        >
          <div className="alert alert-info tx-13">
            <strong>Supported format:</strong> CSV with columns: <code>date, description, debit, credit</code> (or dr/cr, narration, particulars). Most banks export this format.
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Upload CSV File</label>
            <input type="file" className="form-control" accept=".csv,.txt" ref={fileRef} onChange={handleFileImport} />
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Or Paste CSV Data</label>
            <textarea className="form-control font-monospace" rows={8} value={importText} onChange={(e) => setImportText(e.target.value)} placeholder={"date,description,debit,credit\n01/06/2025,School Fees,0,5000000\n05/06/2025,Salary Payment,2500000,0"} style={{ fontSize: "0.8rem" }} />
          </div>
        </AppModal>
      )}
    </AppContainer>
  );
};

export default BankReconciliation;
